import { booleanAttr, headingAnchors, numberAttr, type HeadingAnchor } from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { Plugin, PluginKey, type Command, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { nodes } from "../schema";

export interface HeadingEntry extends HeadingAnchor {
  /** Position of the heading node. */
  pos: number;
  collapsed: boolean;
}

/**
 * Headings with their anchor ids (`markdown-dialect.md` §10, computed by
 * `@wiki/core` so the editor, the renderer and the server agree) and their
 * document positions. Headings inside tables are excluded (`editor.md` §13).
 */
export function headingEntries(doc: ProseMirrorNode): HeadingEntry[] {
  const anchors = headingAnchors(doc);
  const entries: HeadingEntry[] = [];
  let index = 0;
  doc.descendants((node, pos, parent) => {
    if (node.type === nodes.table) return false;
    if (node.type !== nodes.heading) return true;
    if (parent?.type === nodes.tableCell || parent?.type === nodes.tableHeader) return false;
    const anchor = anchors[index];
    index += 1;
    if (!anchor) return false;
    entries.push({ ...anchor, pos, collapsed: booleanAttr(node, "collapsed") });
    return false;
  });
  return entries;
}

/** The blocks a collapsed heading hides: up to the next heading of ≤ level. */
function foldedRanges(doc: ProseMirrorNode): { from: number; to: number }[] {
  const ranges: { from: number; to: number }[] = [];
  const hiding: { active: boolean; level: number; from: number; to: number } = {
    active: false,
    level: 0,
    from: 0,
    to: 0,
  };
  let offset = 0;
  for (let index = 0; index < doc.childCount; index += 1) {
    const node = doc.child(index);
    const end = offset + node.nodeSize;
    let opened = false;
    if (node.type === nodes.heading) {
      const level = numberAttr(node, "level") ?? 1;
      if (hiding.active && level <= hiding.level) {
        ranges.push({ from: hiding.from, to: hiding.to });
        hiding.active = false;
      }
      if (!hiding.active && booleanAttr(node, "collapsed")) {
        hiding.active = true;
        hiding.level = level;
        hiding.from = end;
        hiding.to = end;
        opened = true;
      }
    }
    if (hiding.active && !opened) hiding.to = end;
    offset = end;
  }
  if (hiding.active) ranges.push({ from: hiding.from, to: hiding.to });
  return ranges.filter((range) => range.to > range.from);
}

export const headingsKey = new PluginKey("wikiHeadings");

/**
 * Anchor ids are rendered as decorations rather than attributes: the id of a
 * heading depends on every heading before it (`h-<slug>`, `-n` for repeats), so
 * it is a whole-document computation, and writing it into the node would put
 * presentation into `data`.
 */
export function headingsPlugin(): Plugin {
  return new Plugin({
    key: headingsKey,
    props: {
      decorations(state) {
        const decorations: Decoration[] = [];
        for (const entry of headingEntries(state.doc)) {
          const node = state.doc.nodeAt(entry.pos);
          if (!node) continue;
          decorations.push(
            Decoration.node(entry.pos, entry.pos + node.nodeSize, {
              id: entry.id,
              class: entry.collapsed ? "wiki-heading wiki-heading--collapsed" : "wiki-heading",
            }),
          );
        }
        for (const range of foldedRanges(state.doc)) {
          state.doc.nodesBetween(range.from, range.to, (node, pos) => {
            if (pos < range.from || pos + node.nodeSize > range.to) return true;
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, { class: "wiki-folded-block" }),
            );
            return false;
          });
        }
        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}

/** The `#` fold control on a heading. Fold state is not serialized. */
export function toggleHeadingFold(pos: number): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (node?.type !== nodes.heading) return false;
    if (dispatch) {
      dispatch(
        state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          collapsed: booleanAttr(node, "collapsed") ? undefined : true,
        }),
      );
    }
    return true;
  };
}

export function headingAt(state: EditorState, pos: number): ProseMirrorNode | null {
  const node = state.doc.nodeAt(pos);
  return node?.type === nodes.heading ? node : null;
}
